import { UploadError } from './errors';

export interface UploadArgs {
  workerUrl: string;
  bearerToken: string;
  filename: string;
  content: string;
}

export interface UploadOptions {
  maxAttempts?: number;
  baseDelayMs?: number;
}

const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_BASE_DELAY_MS = 1000;

export async function uploadNote(args: UploadArgs, opts: UploadOptions = {}): Promise<void> {
  const maxAttempts = opts.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
  const baseDelayMs = opts.baseDelayMs ?? DEFAULT_BASE_DELAY_MS;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      await attemptUpload(args);
      return;
    } catch (err) {
      if (!(err instanceof UploadError) || !isRetryable(err) || attempt === maxAttempts) {
        throw err;
      }
      if (baseDelayMs > 0) await sleep(baseDelayMs * 2 ** (attempt - 1));
    }
  }
}

async function attemptUpload(args: UploadArgs): Promise<void> {
  const endpoint = joinUrl(args.workerUrl, 'notes');

  let response: Response;
  try {
    response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${args.bearerToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ filename: args.filename, content: args.content }),
    });
  } catch (err) {
    throw new UploadError(
      'network',
      `Could not reach the relay worker at ${endpoint}.`,
      String(err),
    );
  }

  if (response.ok) return;

  const bodyText = await safeText(response);
  const serverMessage = extractServerMessage(bodyText);

  switch (response.status) {
    case 401:
      throw new UploadError(
        'auth',
        'The relay worker rejected the bearer token. Check the machine settings.',
        bodyText,
      );
    case 400:
      throw new UploadError(
        'validation',
        serverMessage || 'The relay worker rejected the upload as invalid.',
        bodyText,
      );
    case 413:
      throw new UploadError('payload_too_large', 'Note is too large to upload.', bodyText);
    case 429:
      throw new UploadError('rate_limited', 'Rate limited — will retry.', bodyText);
    default:
      throw new UploadError(
        response.status >= 500 ? 'server' : 'unknown',
        `${endpoint} returned HTTP ${response.status}. ${serverMessage || bodyText.slice(0, 200)}`,
        bodyText,
      );
  }
}

function extractServerMessage(raw: string): string {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw) as { message?: unknown };
    if (typeof parsed.message === 'string') return parsed.message;
  } catch {
    /* not JSON — fall through */
  }
  return '';
}

async function safeText(response: Response): Promise<string> {
  try {
    return (await response.text()).slice(0, 500);
  } catch {
    return '';
  }
}

function isRetryable(err: UploadError): boolean {
  return err.code === 'network' || err.code === 'server' || err.code === 'rate_limited';
}

function joinUrl(base: string, path: string): string {
  const trimmedBase = base.replace(/\/+$/, '');
  const trimmedPath = path.replace(/^\/+/, '');
  return `${trimmedBase}/${trimmedPath}`;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
